import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import TableContainer from '../TableContainer';
import useDefaultModal from '../hooks/useDefaultModal';
import useConfirmModal from '../hooks/useConfirmModal';
import ConfirmModal from './ConfirmModal';
import DefaultModal from './DefaultModal';
import usePagination10 from '../hooks/usePagination10';
import PageContent from '../PageContent';
import CompraDiretaItemModal from './CompraDiretaItemModal';
import DataTable from '../DataTable';
import PaginationBar from '../PaginationBar';
import { formatQuantidade } from '../../../service/utils/formatQuantidade';

function CompraDiretaItensModal({ isOpen, onClose, compra, onSave }) {
  const [itens, setItens] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [atualizar, setAtualizar] = useState(false);
  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const token = localStorage.getItem('token');
  const [isLoading, setLoading] = useState(true);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const compraIdRef = useRef(null);

  useEffect(() => {
    const id = compra?.id || compraIdRef.current;
    if (id) {
      compraIdRef.current = id;
      carregarItens(id);
    }
  }, [compra, atualizar]);

  function carregarItens(idCompra) {
    setLoading(true);
    axios
      .get(`${API_BASE_URL.COMPRA_DIRETA_ITEM}/${idCompra}/itens`, { headers: { Authorization: `Bearer ${token}` } })
      .then(result => setItens(result.data))
      .catch(error => {
        console.error('Erro ao obter itens da Compra Direta:', error);
        showDefaultModal('error', 'Erro ao carregar itens da Compra Direta.');
      })
      .finally(() => setLoading(false));
  }

  function handleDelete(item) {
    showConfirmModal('delete', () => handleConfirmDelete(item));
  }

  function handleConfirmDelete(item) {
    const id = compraIdRef.current ?? compra?.id;
    if (!id) return;
    axios
      .delete(`${API_BASE_URL.COMPRA_DIRETA_ITEM}/${id}/itens/${item.sequencial}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        showDefaultModal('success', 'Item excluído com sucesso!');
        setItemSelecionado(null);
        setAtualizar(prev => !prev);
        if (onSave) onSave();
      })
      .catch(error => {
        console.error('Erro ao excluir item:', error);
        showDefaultModal('error', 'Erro ao excluir o item!');
      });
  }

  function handleEdit(item) {
    setItemSelecionado(item);
    setShowModal(true);
  }

  function handleNew() {
    setItemSelecionado(null);
    setShowModal(true);
  }

  function handleCloseModal() {
    setShowModal(false);
    setItemSelecionado(null);
  }

  function handleSaveSuccess() {
    setAtualizar(prev => !prev);
    setItemSelecionado(null);
    if (onSave) onSave();
  }

  const normalizeString = str =>
    str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();

  const itensFiltrados = Array.isArray(itens)
    ? itens.filter(item => {
        const b = normalizeString(filtro);
        return (
          normalizeString(item.descricao || '').includes(b) ||
          normalizeString(item.marca || '').includes(b) ||
          normalizeString(item.unidade || '').includes(b)
        );
      })
    : [];

  const totalItems = itensFiltrados.length;
  const { setPagination } = usePagination10(totalItems);
  const currentItems = itensFiltrados.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  const calcularTotalGeral = () =>
    itensFiltrados.reduce((total, item) => total + ((item.quantidade || 0) * (item.valorUnitario || 0)), 0);

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg" style={{ maxWidth: '95vw', height: '90vh' }}>
        <div className="modal-content h-100 overflow-hidden">
          <div
            className="modal-header text-white"
            style={{
              background: 'linear-gradient(45deg, rgb(7, 29, 65) 0%, rgba(29, 100, 216, 1) 100%)',
              padding: '0.5rem 1rem',
              borderTopLeftRadius: '0.5rem',
              borderTopRightRadius: '0.5rem',
              border: 'none',
            }}
          >
            <h6 className="modal-title mb-0">
              <i className="bi bi-list-ul me-2"></i>
              Itens da Compra Direta {compra?.numCompra}
            </h6>
            <button type="button" className="btn-close btn-close-white" onClick={onClose} title="Fechar"></button>
          </div>
          <PageContent>
            <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-2 mb-2">
              <div className="botao-grid-2col">
                <button
                  className="btn btn-success btn-sm botao-igual"
                  onClick={handleNew}
                  title="Novo item"
                >
                  <i className="bi bi-plus-circle me-1"></i>Novo
                </button>
                <button
                  className="btn btn-primary btn-sm botao-igual"
                  disabled={!itemSelecionado}
                  onClick={() => handleEdit(itemSelecionado)}
                  title={!itemSelecionado ? 'Selecione um item' : 'Editar item selecionado'}
                >
                  <i className="bi bi-pencil me-1"></i>Editar
                </button>
                <button
                  className="btn btn-danger btn-sm botao-igual"
                  disabled={!itemSelecionado}
                  onClick={() => handleDelete(itemSelecionado)}
                  title={!itemSelecionado ? 'Selecione um item' : 'Excluir item selecionado'}
                >
                  <i className="bi bi-trash me-1"></i>Excluir
                </button>
              </div>
              <div className="col-12 col-lg-2 ms-lg-auto">
                <div className="input-group input-group-sm">
                  <span className="input-group-text"><i className="bi bi-search"></i></span>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Buscar..."
                    title="Buscar por descrição, marca, unidade"
                    value={filtro}
                    onChange={e => setFiltro(e.target.value.toUpperCase())}
                  />
                </div>
              </div>
            </div>

            <TableContainer isLoading={isLoading}>
              <DataTable style={{ fontSize: '0.8rem' }} head={
                <tr>
                  <th scope="col" className="px-2 text-center align-middle">Sequencial</th>
                  <th scope="col" className="px-2 text-center align-middle">Descrição</th>
                  <th scope="col" className="px-2 text-center align-middle">Marca</th>
                  <th scope="col" className="px-2 text-center align-middle">Unidade</th>
                  <th scope="col" className="px-2 text-center align-middle">Quantidade</th>
                  <th scope="col" className="px-2 text-center align-middle">Valor Unit.</th>
                  <th scope="col" className="px-2 text-center align-middle">Valor Total</th>
                </tr>}>
                <tbody>
                  {currentItems.map(item => (
                    <tr
                      key={item.sequencial}
                      className={itemSelecionado && itemSelecionado.sequencial === item.sequencial ? 'table-primary' : ''}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setItemSelecionado(item)}
                      onDoubleClick={() => { setItemSelecionado(item); handleEdit(item); }}
                    >
                      <td className="px-2">{item.sequencial}</td>
                      <td className="px-2">{item.descricao}</td>
                      <td className="px-2">{item.marca}</td>
                      <td className="px-2">{item.unidade}</td>
                      <td className="px-2 text-end">{formatQuantidade(item.quantidade)}</td>
                      <td className="px-2 text-end">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.valorUnitario || 0)}
                      </td>
                      <td className="px-2 text-end">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format((item.quantidade || 0) * (item.valorUnitario || 0))}
                      </td>
                    </tr>
                  ))}
                  {currentItems.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center py-3 text-muted">
                        <i className="bi bi-inbox fs-3 d-block mb-2"></i>
                        Nenhum item encontrado
                      </td>
                    </tr>
                  )}
                </tbody>
                {itensFiltrados.length > 0 && (
                  <tfoot className="fw-bold" style={{ position: 'sticky', bottom: 0, zIndex: 2, backgroundColor: '#f8f9fa', isolation: 'isolate' }}>
                    <tr>
                      <td colSpan="6" className="px-2 text-end">TOTAL GERAL:</td>
                      <td className="px-2 text-end">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(calcularTotalGeral())}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </DataTable>
            </TableContainer>
          </PageContent>
          <PaginationBar setPagination={setPagination} />
        </div>
      </div>

      {showModal && (
        <CompraDiretaItemModal show={showModal} item={itemSelecionado} compra={compra} onClose={handleCloseModal} onSave={handleSaveSuccess} />
      )}

      <ConfirmModal {...setConfirmModal} />
      <DefaultModal {...setDefaultModal} />
    </div>
  );
}

export default CompraDiretaItensModal;
